package org.lennycodes.gradematewebsite.components

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.onMouseEnter
import com.varabyte.kobweb.compose.ui.modifiers.onMouseLeave
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.icons.fa.FaIcon
import com.varabyte.kobweb.silk.components.icons.fa.IconCategory
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.lennycodes.gradematewebsite.components.style.CardStyle
import org.lennycodes.gradematewebsite.components.style.DownloadsGridStyle
import org.lennycodes.gradematewebsite.components.style.EyebrowStyle
import org.lennycodes.gradematewebsite.components.style.GradeMateColors
import org.lennycodes.gradematewebsite.components.style.SectionHeadingStyle
import org.lennycodes.gradematewebsite.components.style.SectionInnerStyle
import org.lennycodes.gradematewebsite.components.style.SectionStyle
import org.lennycodes.gradematewebsite.components.style.SectionSubtitleStyle
import org.lennycodes.gradematewebsite.components.style.toRgba
import org.lennycodes.gradematewebsite.fetchDownloads
import org.lennycodes.gradematewebsite.util.DownloadOption
import org.w3c.dom.events.Event

private const val Ease = "cubic-bezier(0.22, 1, 0.36, 1)"

private fun prefersReducedMotion(): Boolean =
    window.matchMedia("(prefers-reduced-motion: reduce)").matches

private fun detectPlatform(): String? {
    val ua = window.navigator.userAgent.lowercase()
    return when {
        "android" in ua -> "Android"
        "iphone" in ua || "ipad" in ua -> "iOS"
        "windows" in ua -> "Windows"
        "mac os" in ua || "macintosh" in ua -> "macOS"
        "linux" in ua || "x11" in ua -> "Linux"
        else -> null
    }
}

@Composable
private fun Reveal(
    id: String,
    modifier: Modifier = Modifier,
    delayMs: Int = 0,
    content: @Composable () -> Unit
) {
    var visible by remember { mutableStateOf(false) }
    val reduce = remember { prefersReducedMotion() }

    DisposableEffect(Unit) {
        val check = {
            val top = document.getElementById(id)?.getBoundingClientRect()?.top
            if (reduce || (top != null && top < window.innerHeight * 0.9)) {
                visible = true
            }
        }
        val onScroll: (Event) -> Unit = { check() }
        window.addEventListener("scroll", onScroll)
        check()
        onDispose { window.removeEventListener("scroll", onScroll) }
    }

    Box(
        modifier
            .id(id)
            .fillMaxWidth()
            .styleModifier {
                val delay = if (reduce) 0 else delayMs
                val duration = if (reduce) 0 else 800
                property("opacity", if (visible) "1" else "0")
                property("transform", if (visible) "translateY(0)" else "translateY(24px)")
                property(
                    "transition",
                    "opacity ${duration}ms ease ${delay}ms, " +
                            "transform ${duration}ms $Ease ${delay}ms"
                )
            }
    ) {
        content()
    }
}

val DownloadCardStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .padding(30.px)
            .styleModifier {
                property("height", "100%")
                property("box-sizing", "border-box")
                property(
                    "transition",
                    "transform 350ms $Ease, " +
                            "box-shadow 350ms ease, " +
                            "border-color 350ms ease"
                )
            }
    }
    hover {
        Modifier.styleModifier {
            property("transform", "translateY(-5px)")
        }
    }
}

@Composable
fun DownloadsSection() {
    var downloads by remember { mutableStateOf<List<DownloadOption>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var errorMessage by remember { mutableStateOf("") }
    var reloadKey by remember { mutableStateOf(0) }
    val recommended = remember { detectPlatform() }

    LaunchedEffect(reloadKey) {
        isLoading = true
        errorMessage = ""
        try {
            downloads = fetchDownloads()
        } catch (e: Throwable) {
            errorMessage = "Couldn't load downloads: ${e.message}"
        } finally {
            isLoading = false
        }
    }

    Box(
        SectionStyle.toModifier().id("downloads")
    ) {
        Column(SectionInnerStyle.toModifier()) {

            // ---------- Header ----------
            Reveal(id = "downloads-header") {
                Column(Modifier.fillMaxWidth()) {

                    P(
                        EyebrowStyle
                            .toModifier()
                            .margin(0.px)
                            .styleModifier {
                                property("letter-spacing", "0.16em")
                                property("text-transform", "uppercase")
                                property("font-size", "12px")
                                property("font-weight", "700")
                                property("opacity", "0.72")
                            }
                            .toAttrs()
                    ) {
                        Text("Downloads")
                    }

                    P(
                        SectionHeadingStyle
                            .toModifier()
                            .margin(top = 10.px, bottom = 14.px)
                            .styleModifier {
                                property("font-size", "clamp(34px, 5vw, 54px)")
                                property("letter-spacing", "-0.045em")
                                property("line-height", "1.02")
                                property("font-weight", "750")
                            }
                            .toAttrs()
                    ) {
                        Text("Get GradeMate")
                    }

                    P(
                        SectionSubtitleStyle
                            .toModifier()
                            .margin(0.px)
                            .styleModifier {
                                property("max-width", "600px")
                                property("line-height", "1.75")
                                property("font-size", "15px")
                                property("opacity", "0.78")
                            }
                            .toAttrs()
                    ) {
                        Text(
                            "Download the latest version for your device. " +
                                    "Fast, free, and ready when you are."
                        )
                    }
                }
            }

            // ---------- Loading ----------
            if (isLoading) {
                DownloadsSkeleton()
                return@Column
            }

            // ---------- Error ----------
            if (errorMessage.isNotEmpty()) {
                Div(
                    attrs = Modifier
                        .margin(top = 44.px)
                        .styleModifier {
                            property("padding", "24px 26px")
                            property("border-radius", "16px")
                            property("background", "rgba(220, 38, 38, 0.045)")
                            property("border", "1px solid rgba(220, 38, 38, 0.16)")
                            property(
                                "box-shadow",
                                "0 12px 30px -24px rgba(127, 29, 29, 0.4)"
                            )
                        }
                        .toAttrs { attr("role", "alert") }
                ) {
                    P(
                        Modifier
                            .margin(0.px)
                            .fontSize(14.px)
                            .lineHeight(1.65)
                            .styleModifier { property("color", "rgb(185, 28, 28)") }
                            .toAttrs()
                    ) {
                        Text(errorMessage)
                    }

                    Button(
                        attrs = Modifier
                            .margin(top = 16.px)
                            .onClick { reloadKey++ }
                            .styleModifier {
                                property("height", "40px")
                                property("padding", "0 17px")
                                property("border-radius", "10px")
                                property("border", "1px solid rgba(220, 38, 38, 0.2)")
                                property("background", "#ffffff")
                                property("color", "rgb(185, 28, 28)")
                                property("font-family", "inherit")
                                property("font-size", "13px")
                                property("font-weight", "650")
                                property("cursor", "pointer")
                            }
                            .toAttrs { attr("type", "button") }
                    ) {
                        Text("Try again")
                    }
                }
                return@Column
            }

            // ---------- Empty ----------
            if (downloads.isEmpty()) {
                Div(
                    attrs = Modifier
                        .margin(top = 44.px)
                        .styleModifier {
                            property("padding", "48px 24px")
                            property("text-align", "center")
                            property("border-radius", "18px")
                            property(
                                "border",
                                "1px dashed ${GradeMateColors.Primary.toRgba(alpha = 0.24f)}"
                            )
                            property(
                                "background",
                                GradeMateColors.Primary.toRgba(alpha = 0.025f)
                            )
                        }
                        .toAttrs()
                ) {
                    P(
                        Modifier
                            .margin(0.px)
                            .fontSize(14.px)
                            .color(GradeMateColors.TextSecondary)
                            .toAttrs()
                    ) {
                        Text("No downloads are available right now.")
                    }
                }
                return@Column
            }

            // ---------- Grid ----------
            Div(
                attrs = DownloadsGridStyle
                    .toModifier()
                    .margin(top = 48.px)
                    .styleModifier { property("align-items", "stretch") }
                    .toAttrs()
            ) {
                downloads.forEachIndexed { index, option ->
                    DownloadCard(
                        option = option,
                        index = index,
                        isRecommended = option.platform == recommended
                    )
                }
            }
        }
    }
}

@Composable
private fun DownloadCard(
    option: DownloadOption,
    index: Int,
    isRecommended: Boolean
) {
    var hovered by remember { mutableStateOf(false) }
    val reduce = remember { prefersReducedMotion() }
    fun t(ms: Int): Int = if (reduce) 0 else ms

    val iconName = when (option.platform) {
        "Android" -> "android"
        "Windows" -> "windows"
        "Linux" -> "linux"
        "macOS" -> "apple"
        "iOS" -> "apple"
        else -> "download"
    }

    Reveal(
        id = "download-card-$index",
        modifier = Modifier.styleModifier { property("height", "100%") },
        delayMs = index * 100
    ) {
        Column(
            CardStyle
                .toModifier()
                .then(DownloadCardStyle.toModifier())
                .onMouseEnter { hovered = true }
                .onMouseLeave { hovered = false }
                .styleModifier {
                    property("position", "relative")
                    property("overflow", "hidden")

                    property(
                        "border",
                        "1px solid ${
                            option.accent.toRgba(
                                alpha = if (hovered) 0.28f else 0.13f
                            )
                        }"
                    )

                    property(
                        "background",
                        "linear-gradient(145deg, rgba(255,255,255,0.99), rgba(248,250,252,0.96))"
                    )

                    property(
                        "box-shadow",
                        if (hovered)
                            "0 28px 65px -32px ${option.accent.toRgba(alpha = 0.48f)}"
                        else
                            "0 14px 38px -26px rgba(15, 23, 42, 0.22)"
                    )

                    property(
                        "transform",
                        if (hovered) "translateY(-5px)" else "translateY(0)"
                    )

                    property(
                        "transition",
                        "transform ${t(350)}ms $Ease, " +
                                "box-shadow ${t(350)}ms ease, " +
                                "border-color ${t(350)}ms ease"
                    )
                }
        ) {

            // ---------- Recommended badge ----------
            if (isRecommended) {
                Div(
                    attrs = Modifier
                        .styleModifier {
                            property("position", "absolute")
                            property("top", "18px")
                            property("right", "18px")
                            property("display", "inline-flex")
                            property("align-items", "center")
                            property("gap", "6px")
                            property("padding", "6px 10px")
                            property("border-radius", "999px")
                            property("background", option.accent.toRgba(alpha = 0.09f))
                            property(
                                "border",
                                "1px solid ${option.accent.toRgba(alpha = 0.16f)}"
                            )
                            property("color", option.accent.toRgba())
                            property("font-size", "10px")
                            property("font-weight", "750")
                            property("letter-spacing", "0.06em")
                            property("text-transform", "uppercase")
                        }
                        .toAttrs()
                ) {
                    // Dot
                    Div(
                        attrs = Modifier
                            .styleModifier {
                                property("width", "5px")
                                property("height", "5px")
                                property("border-radius", "50%")
                                property("background", option.accent.toRgba())
                            }
                            .toAttrs()
                    ) {}

                    // Text
                    Text("Recommended")
                }
            }

            // ---------- Platform icon ----------
            Box(
                Modifier
                    .size(52.px)
                    .borderRadius(14.px)
                    .styleModifier {
                        property(
                            "background",
                            option.accent.toRgba(
                                alpha = if (hovered) 0.12f else 0.075f
                            )
                        )
                        property(
                            "border",
                            "1px solid ${option.accent.toRgba(alpha = 0.10f)}"
                        )
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                        property(
                            "transform",
                            if (hovered) "scale(1.06) rotate(-3deg)"
                            else "scale(1) rotate(0deg)"
                        )
                        property(
                            "transition",
                            "transform ${t(450)}ms cubic-bezier(0.34, 1.56, 0.64, 1), " +
                                    "background ${t(300)}ms ease"
                        )
                    }
            ) {
                FaIcon(
                    name = iconName,
                    style = IconCategory.BRAND,
                    modifier = Modifier
                        .fontSize(24.px)
                        .color(option.accent)
                )
            }

            // ---------- Platform info ----------
            Div(
                attrs = Modifier
                    .margin(top = 22.px)
                    .styleModifier {
                        property("display", "flex")
                        property("flex-direction", "column")
                        property("gap", "7px")
                    }
                    .toAttrs()
            ) {

                P(
                    Modifier
                        .margin(0.px)
                        .fontSize(21.px)
                        .fontWeight(FontWeight.Bold)
                        .color(GradeMateColors.TextPrimary)
                        .styleModifier {
                            property("letter-spacing", "-0.03em")
                            property("line-height", "1.2")
                        }
                        .toAttrs()
                ) {
                    Text(option.platform)
                }

                // Version · Size
                Div(
                    attrs = Modifier
                        .styleModifier {
                            property("display", "flex")
                            property("align-items", "center")
                            property("gap", "8px")
                            property("font-size", "13px")
                            property("font-weight", "500")
                            property("color", "${GradeMateColors.TextSecondary}")
                        }
                        .toAttrs()
                ) {
                    Text(option.version)

                    Div(
                        attrs = Modifier
                            .styleModifier { property("opacity", "0.35") }
                            .toAttrs()
                    ) {
                        Text("·")
                    }

                    Text(option.size)
                }
            }

            // ---------- Description ----------
            P(
                Modifier
                    .margin(top = 20.px, bottom = 0.px)
                    .fontSize(14.px)
                    .lineHeight(1.7)
                    .color(GradeMateColors.TextSecondary)
                    .styleModifier {
                        property("overflow-wrap", "break-word")
                        property("max-width", "92%")
                        property("flex-grow", "1")
                    }
                    .toAttrs()
            ) {
                Text(option.description)
            }

            // ---------- CTA ----------
            Link(
                option.href,
                modifier = Modifier
                    .margin(top = 28.px)
                    .styleModifier {
                        property("display", "flex")
                        property("align-items", "center")
                        property("gap", "10px")
                        property("width", "100%")
                        property("height", "52px")
                        property("box-sizing", "border-box")
                        property("padding", "0 17px")
                        property("border-radius", "13px")
                        property("text-decoration", "none")
                        property("color", "#ffffff")
                        property("font-size", "14px")
                        property("font-weight", "650")
                        property("letter-spacing", "-0.01em")
                        property("background", option.accent.toRgba())

                        property(
                            "box-shadow",
                            if (hovered)
                                "0 14px 30px -12px ${option.accent.toRgba(alpha = 0.65f)}"
                            else
                                "0 7px 18px -10px ${option.accent.toRgba(alpha = 0.48f)}"
                        )

                        property(
                            "transition",
                            "box-shadow ${t(300)}ms ease, " +
                                    "transform ${t(300)}ms $Ease"
                        )

                        property("outline", "none")
                    }

            ) {

                FaIcon(
                    name = "download",
                    style = IconCategory.SOLID,
                    modifier = Modifier.fontSize(13.px)
                )

                Text("Download")

                Div(
                    attrs = Modifier
                        .styleModifier {
                            property("margin-left", "auto")
                            property("font-size", "17px")
                            property("line-height", "1")
                            property(
                                "transform",
                                if (hovered) "translateX(3px)" else "translateX(0)"
                            )
                            property("transition", "transform ${t(300)}ms $Ease")
                        }
                        .toAttrs()
                ) {
                    Text("→")
                }
            }
        }
    }
}

@Composable
private fun DownloadsSkeleton() {
    val reduce = remember { prefersReducedMotion() }

    Div(
        attrs = DownloadsGridStyle
            .toModifier()
            .margin(top = 48.px)
            .styleModifier { property("align-items", "stretch") }
            .toAttrs {
                attr("role", "status")
                attr("aria-label", "Loading downloads")
            }
    ) {
        repeat(3) {
            Column(
                CardStyle
                    .toModifier()
                    .padding(30.px)
                    .fillMaxWidth()
                    .styleModifier {
                        property("border-radius", "18px")
                        property("border", "1px solid rgba(15, 23, 42, 0.07)")
                        property("background", "#ffffff")
                        property("box-shadow", "0 14px 38px -26px rgba(15, 23, 42, 0.18)")
                    }
            ) {
                SkeletonBlock("52px", "52px", "14px", reduce = reduce)
                SkeletonBlock("42%", "20px", "6px", marginTop = "22px", reduce = reduce)
                SkeletonBlock("34%", "13px", "5px", marginTop = "10px", reduce = reduce)
                SkeletonBlock("92%", "12px", "5px", marginTop = "22px", reduce = reduce)
                SkeletonBlock("76%", "12px", "5px", marginTop = "8px", reduce = reduce)
                SkeletonBlock("100%", "52px", "13px", marginTop = "28px", reduce = reduce)
            }
        }
    }
}

@Composable
private fun SkeletonBlock(
    width: String,
    height: String,
    radius: String,
    marginTop: String? = null,
    reduce: Boolean
) {
    Div(
        attrs = Modifier
            .styleModifier {
                property("width", width)
                property("height", height)
                if (marginTop != null) property("margin-top", marginTop)
                property("border-radius", radius)
                property(
                    "background",
                    if (reduce) "rgba(15, 23, 42, 0.07)"
                    else "linear-gradient(90deg, " +
                            "rgba(15,23,42,0.055) 0%, " +
                            "rgba(15,23,42,0.10) 50%, " +
                            "rgba(15,23,42,0.055) 100%)"
                )
                if (!reduce) {
                    property("background-size", "200% 100%")
                    property(
                        "animation",
                        "grademate-skeleton-shimmer 1.8s ease-in-out infinite"
                    )
                }
            }
            .toAttrs()
    )
}